import { swivuRecentRecordSortFn } from "@/modern/utils";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { ENTITY_TYPES, VIEW_TYPES } from "@/other/constants/OtherKeys";
import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import type { Resource } from "@/bryntum-lib/models/Resource";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { EntityFinderItemRow, entityFinderTintText } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import { renderEntityFinderConfigIcon } from "@/modern/components/entity-finder/components/renderEntityFinderConfigIcon";
import {
  canCreate,
  estimateDefaultRowHeight,
  openEntityEditor,
} from "@/modern/components/entity-finder/constants/config/shared";
import type { EntityFinderConfigEntry } from "@/modern/components/entity-finder/types";
import {
  defaultEntityListSection,
  defaultMatchesScope,
  matchesSearch,
  passesArchiveFilter,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";

function mapResource(resource: Resource) {
  return {
    id: `resource-${resource.id}`,
    type: "resource" as const,
    label: resource.name || "N/A",
    subtitle: resource.email || undefined,
    record: resource,
  };
}

export const resourceConfig: EntityFinderConfigEntry<"resource"> = {
  type: "resource",
  label: "Resource",
  plural: "Resources",
  icon: <i className={SWIVU_STANDARD_ICONS[VIEW_TYPES.resources]} aria-hidden />,
  tint: "bg-chart-3/15 text-chart-3",
  listOrder: 20,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "resource"),
  getData: (ctx) => {
    const searchLower = ctx.debouncedQuery.toLowerCase();
    const records = swivuRecentRecordSortFn(
      ctx.project.resourceStore.query(
        (resource: Resource) =>
          passesArchiveFilter(resource.archived, ctx.isShowArchive) &&
          !resource.isLinked &&
          (matchesSearch(resource.name, searchLower) || matchesSearch(resource.email, searchLower)),
        true
      ) as unknown as Resource[],
      { currentBoardUserId: ctx.currentBoardUserId }
    );
    return records.map(mapResource);
  },
  getRecentItem: (ctx) => {
    const records = swivuRecentRecordSortFn(
      ctx.project.resourceStore.query(
        (r: Resource) => passesArchiveFilter(r.archived, ctx.isShowArchive) && !r.isLinked,
        true
      ) as unknown as Resource[],
      { currentBoardUserId: ctx.currentBoardUserId }
    ).slice(0, 1);
    return records[0] ? mapResource(records[0]) : null;
  },
  buildListSection: (items, _ctx) => defaultEntityListSection(resourceConfig, items),
  estimateRowHeight: estimateDefaultRowHeight,
  itemRenderer: ({ item, query, entry }) => (
    <EntityFinderItemRow
      icon={renderEntityFinderConfigIcon(entry.icon, 14, "text-sm")}
      iconClassName={entityFinderTintText(resourceConfig.tint)}
      title={<EntityFinderHighlight text={item.label} query={query} />}
      subtitle={
        item.subtitle ? (
          <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
        ) : undefined
      }
    />
  ),
  onSelect: (item, ctx) => {
    ctx.entityManager.openEntityManager({
      entityType: ENTITY_TYPES.RESOURCE,
      entityId: (item.record as Resource).id,
    });
  },
  createOnUnmatched: {
    label: "Create new resource",
    fn: (query, ctx) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      const newRecord = (
        ctx.project.resourceStore as { createRecord: (data?: object) => unknown }
      ).createRecord({ name: trimmed, type: null });
      openEntityEditor(ctx, EDITOR_KEYS.resourceEditorV2, newRecord, { resource: newRecord });
    },
    isEnabled: canCreate,
  },
};
